import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * Keeps the pre-#354 capacity deep links working.
 *
 * Capacity used to live at ``/networks/capacity`` and
 * ``/networks/capacity/:networkId``. It moved out from under the
 * ``/networks/`` prefix because the two backend surfaces disagree about who
 * may read them: ``GET /crm/networks/overview/`` is a bare
 * ``@CustomerRequired()`` (customer admin), while both capacity endpoints are
 * explicitly ``@CustomerRequired(admin_required=False)`` — open to any CRM
 * user by a deliberate backend decision. While capacity sat under
 * ``/networks/``, gating the Networks leaf would have dragged capacity under
 * the same guard and hidden a page non-admins may legitimately read, so
 * ``/networks`` could not be gated at all (#354 item 1).
 *
 * Those URLs are bookmarked and pasted into chat, so they must keep resolving
 * rather than 404. This component forwards them to the new ``/capacity``
 * paths, carrying the network id, the query string and the hash across.
 *
 * It is deliberately **unguarded** in ``App.tsx`` even though it sits under
 * the now-``customerAdminOnly`` ``/networks`` leaf: it renders no page and
 * issues no request, and guarding it would turn an old bookmark into "Access
 * Denied" for exactly the non-admin users capacity is open to.
 * ``navConfig.test.ts`` knows about this and skips redirect-only routes when
 * it checks that sub-routes inherit their leaf's guard.
 */

const LEGACY_PREFIX = '/networks/capacity';
const NEW_PREFIX = '/capacity';

/**
 * Map a legacy capacity URL onto its replacement, preserving the network id,
 * the query string and the hash. Pure so it can be tested without a router.
 */
export function legacyCapacityTarget(pathname: string, search = '', hash = ''): string {
  const suffix = pathname.startsWith(LEGACY_PREFIX) ? pathname.slice(LEGACY_PREFIX.length) : '';
  return `${NEW_PREFIX}${suffix}${search}${hash}`;
}

export const LegacyCapacityRedirect: React.FC = () => {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={legacyCapacityTarget(pathname, search, hash)} replace />;
};

export default LegacyCapacityRedirect;
